import { useCallback, useContext, useEffect, useRef, useState, useMemo } from 'react';
import {
    Box,
    Button,
    Card,
    ChoiceList,
    EmptySearchResult,
    IndexTable,
    IndexFilters,
    Layout,
    Page,
    Text,
    TextField,
    useBreakpoints,
    useSetIndexFiltersMode,
    Pagination,
    InlineStack,
    Tooltip,
} from '@shopify/polaris';
import { ViewIcon } from '@shopify/polaris-icons';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { SessionContext } from '#client/contexts/SessionContext';
import { hasPermission } from '#client/utils/helpers';
import { useToast } from '#client/components/common/Toast/ToastContext';
import { cycleCountService } from '#client/services/cycleCount/cycleCountService';
import { STATUS_OPTIONS, TYPE_OPTIONS, SORT_OPTIONS, STATUS_DISPLAY_MAPPING, RESOURCE_NAME, TYPE_DISPLAY_MAPPING, STATUS } from '#client/pages/CycleCounts/constants/cycleCount';
import { loadFiltersFromSession, saveFiltersToSession, FILTER_STORAGE_KEY } from '#client/utils/helpers/cyclecounts';

// Import components
import CustomBadge from '#client/components/common/CustomBadge/CustomBadge';

const CycleCountsDashboard = () => {
    const navigate = useNavigate();
    const { currentUser } = useContext(SessionContext);
    const { addToast } = useToast();
    const breakpoints = useBreakpoints();

    // Load saved filters from sessionStorage
    const savedFilters = loadFiltersFromSession();

    const { mode, setMode } = useSetIndexFiltersMode('FILTERING');

    // Pagination state
    const [pagination, setPagination] = useState({
        page: savedFilters?.page || 1,
        pageSize: 12,
        totalCount: 0,
        totalPages: 0,
        hasNext: false,
        hasPrevious: false,
    });

    // Filter state - initialize from saved filters if available
    const [queryValue, setQueryValue] = useState(savedFilters?.queryValue || '');
    const [statusFilter, setStatusFilter] = useState(savedFilters?.statusFilter || []);
    const [typeFilter, setTypeFilter] = useState(savedFilters?.typeFilter || []);
    const [storeFilter, setStoreFilter] = useState(savedFilters?.storeFilter || []);
    const [createdDateFrom, setCreatedDateFrom] = useState(savedFilters?.createdDateFrom || '');
    const [createdDateTo, setCreatedDateTo] = useState(savedFilters?.createdDateTo || '');
    const [updatedDateFrom, setUpdatedDateFrom] = useState(savedFilters?.updatedDateFrom || '');
    const [updatedDateTo, setUpdatedDateTo] = useState(savedFilters?.updatedDateTo || '');
    const [sortSelected, setSortSelected] = useState(savedFilters?.sortSelected || ['createdAt desc']);

    // Cycle counts data state
    const [cycleCounts, setCycleCounts] = useState([]);

    // Has permission to create new cycle counts
    const canCreatePrescribe = hasPermission(currentUser, 'ccCreatePrescribe');
    const canCreateAdHoc = hasPermission(currentUser, 'ccCreateAdHoc');
    const canCreateCycleCount = canCreatePrescribe || canCreateAdHoc;

    // Track previous activeStore value
    const prevActiveStoreRef = useRef(null);

    // Auto-save filters to sessionStorage whenever they change
    useEffect(() => {
        const filters = {
            queryValue,
            statusFilter,
            typeFilter,
            storeFilter,
            createdDateFrom,
            createdDateTo,
            updatedDateFrom,
            updatedDateTo,
            sortSelected,
            page: pagination.page,
        };
        saveFiltersToSession(filters);
    }, [queryValue, statusFilter, typeFilter, storeFilter, createdDateFrom, createdDateTo, updatedDateFrom, updatedDateTo, sortSelected, pagination.page]);

    // Reset pagination to page 1 when activeStore changes
    useEffect(() => {
        const currentActiveStore = currentUser?.activeStore;

        if (prevActiveStoreRef.current !== currentActiveStore && prevActiveStoreRef.current !== null) {
            prevActiveStoreRef.current = currentActiveStore;
            setPagination((prev) => ({ ...prev, page: 1 }));
        } else if (prevActiveStoreRef.current === null) {
            prevActiveStoreRef.current = currentActiveStore;
        }
    }, [currentUser?.activeStore]);

    // Memoize query parameters for stable query key
    const queryParams = useMemo(() => {
        const sortValue = sortSelected && sortSelected[0] && typeof sortSelected[0] === 'string' ? sortSelected[0] : 'createdAt desc';
        const sortParts = sortValue.split(' ');
        const sortField = sortParts[0] || 'createdAt';
        const sortDir = sortParts[1] || 'desc';
        const UTCOffset = -new Date().getTimezoneOffset() / 60;

        return {
            page: pagination.page,
            pageSize: pagination.pageSize,
            sortField,
            sortDir,
            filters: {
                query: queryValue,
                status: statusFilter,
                type: typeFilter,
                location: storeFilter,
                createdDateFrom,
                createdDateTo,
                updatedDateFrom,
                updatedDateTo,
                UTCOffset,
            },
        };
    }, [pagination.page, pagination.pageSize, sortSelected, queryValue, statusFilter, typeFilter, storeFilter, createdDateFrom, createdDateTo, updatedDateFrom, updatedDateTo]);

    // Fetch cycle counts using TanStack Query
    const { data, isLoading, error } = useQuery({
        queryKey: ['cycle-counts', currentUser?.activeStore || [], queryParams],
        queryFn: async () => {
            const response = await cycleCountService.getAll(queryParams.page, queryParams.pageSize, queryParams.sortField, queryParams.sortDir, queryParams.filters);
            return response.data;
        },
        enabled: !!currentUser,
        staleTime: 30000, // 30 seconds
    });

    // Update cycle counts and pagination when data changes
    useEffect(() => {
        if (data?.pagination) {
            setPagination(data.pagination);
        }

        if (data?.cycleCounts) {
            setCycleCounts(data.cycleCounts);
        }
    }, [data]);

    // Show error toast if query fails
    useEffect(() => {
        if (error) {
            console.error('Error fetching cycle counts:', error);
            addToast({
                type: 'error',
                message: error.response?.data?.error?.message || 'Failed to fetch cycle counts',
            });
        }
    }, [error, addToast]);

    // Filter handlers
    const handleQueryChange = useCallback((value) => {
        setQueryValue(value);
        setPagination((prev) => ({ ...prev, page: 1 }));
    }, []);

    const handleQueryClear = useCallback(() => {
        setQueryValue('');
        setPagination((prev) => ({ ...prev, page: 1 }));
    }, []);

    const handleStatusChange = useCallback((value) => {
        setStatusFilter(value);
        setPagination((prev) => ({ ...prev, page: 1 }));
    }, []);

    const handleTypeChange = useCallback((value) => {
        setTypeFilter(value);
        setPagination((prev) => ({ ...prev, page: 1 }));
    }, []);

    const handleStoreChange = useCallback((value) => {
        setStoreFilter(value);
        setPagination((prev) => ({ ...prev, page: 1 }));
    }, []);

    const handleStatusRemove = useCallback(() => {
        setStatusFilter([]);
        setPagination((prev) => ({ ...prev, page: 1 }));
    }, []);

    const handleTypeRemove = useCallback(() => {
        setTypeFilter([]);
        setPagination((prev) => ({ ...prev, page: 1 }));
    }, []);

    const handleStoreRemove = useCallback(() => {
        setStoreFilter([]);
        setPagination((prev) => ({ ...prev, page: 1 }));
    }, []);

    const handleCreatedDateFromChange = useCallback((value) => {
        setCreatedDateFrom(value);
        setPagination((prev) => ({ ...prev, page: 1 }));
    }, []);

    const handleCreatedDateToChange = useCallback((value) => {
        setCreatedDateTo(value);
        setPagination((prev) => ({ ...prev, page: 1 }));
    }, []);

    const handleUpdatedDateFromChange = useCallback((value) => {
        setUpdatedDateFrom(value);
        setPagination((prev) => ({ ...prev, page: 1 }));
    }, []);

    const handleUpdatedDateToChange = useCallback((value) => {
        setUpdatedDateTo(value);
        setPagination((prev) => ({ ...prev, page: 1 }));
    }, []);

    const handleCreatedDateRangeRemove = useCallback(() => {
        setCreatedDateFrom('');
        setCreatedDateTo('');
        setPagination((prev) => ({ ...prev, page: 1 }));
    }, []);

    const handleUpdatedDateRangeRemove = useCallback(() => {
        setUpdatedDateFrom('');
        setUpdatedDateTo('');
        setPagination((prev) => ({ ...prev, page: 1 }));
    }, []);

    const handleClearAll = useCallback(() => {
        // Clear sessionStorage when clearing all filters
        sessionStorage.removeItem(FILTER_STORAGE_KEY);
        handleQueryClear();
        handleStatusRemove();
        handleTypeRemove();
        handleStoreRemove();
        handleCreatedDateRangeRemove();
        handleUpdatedDateRangeRemove();
    }, [handleQueryClear, handleStatusRemove, handleTypeRemove, handleStoreRemove, handleCreatedDateRangeRemove, handleUpdatedDateRangeRemove]);

    const handleSortChange = useCallback((value) => {
        // Ensure value is valid before setting
        if (value && Array.isArray(value) && value.length > 0 && value[0]) {
            setSortSelected(value);
            setPagination((prev) => ({ ...prev, page: 1 }));
        }
    }, []);

    const handlePageChange = useCallback((newPage) => {
        setPagination((prev) => ({ ...prev, page: newPage }));
    }, []);

    // Store options from currentUser.locations
    const storeOptions = useMemo(() => {
        if (!currentUser?.locations) return [];
        return currentUser.locations.map((loc) => ({
            label: loc.name,
            value: loc.locationId.toString(),
        }));
    }, [currentUser?.locations]);

    // Filters
    const filters = [
        {
            key: 'status',
            label: 'Status',
            filter: <ChoiceList title="Status" titleHidden choices={STATUS_OPTIONS} selected={statusFilter} onChange={handleStatusChange} allowMultiple />,
            shortcut: true,
        },
        {
            key: 'type',
            label: 'Type',
            filter: <ChoiceList title="Type" titleHidden choices={TYPE_OPTIONS} selected={typeFilter} onChange={handleTypeChange} allowMultiple />,
            shortcut: true,
        },
        {
            key: 'store',
            label: 'Assigned Store',
            filter: <ChoiceList title="Assigned Store" titleHidden choices={storeOptions} selected={storeFilter} onChange={handleStoreChange} allowMultiple />,
            shortcut: true,
        },
        {
            key: 'createdDateRange',
            label: 'Date Created',
            filter: (
                <div>
                    <div style={{ marginBottom: '8px' }}>
                        <TextField label="From" value={createdDateFrom} onChange={handleCreatedDateFromChange} autoComplete="off" type="date" />
                    </div>
                    <div>
                        <TextField label="To" value={createdDateTo} onChange={handleCreatedDateToChange} autoComplete="off" type="date" />
                    </div>
                </div>
            ),
            shortcut: true,
        },
        {
            key: 'updatedDateRange',
            label: 'Date Updated',
            filter: (
                <div>
                    <div style={{ marginBottom: '8px' }}>
                        <TextField label="From" value={updatedDateFrom} onChange={handleUpdatedDateFromChange} autoComplete="off" type="date" />
                    </div>
                    <div>
                        <TextField label="To" value={updatedDateTo} onChange={handleUpdatedDateToChange} autoComplete="off" type="date" />
                    </div>
                </div>
            ),
            shortcut: true,
        },
    ];

    // Applied filters
    const appliedFilters = [];
    if (statusFilter.length > 0) {
        appliedFilters.push({
            key: 'status',
            label: `Status: ${statusFilter.map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')}`,
            onRemove: handleStatusRemove,
        });
    }
    if (typeFilter.length > 0) {
        appliedFilters.push({
            key: 'type',
            label: `Type: ${typeFilter.map((t) => t.charAt(0).toUpperCase() + t.slice(1)).join(', ')}`,
            onRemove: handleTypeRemove,
        });
    }
    if (storeFilter.length > 0) {
        const storeNames = storeFilter.map((locationId) => {
            const location = currentUser?.locations?.find((loc) => loc.locationId.toString() === locationId);
            return location ? location.name : locationId;
        });
        appliedFilters.push({
            key: 'store',
            label: `Store: ${storeNames.join(', ')}`,
            onRemove: handleStoreRemove,
        });
    }
    if (createdDateFrom || createdDateTo) {
        const dateRangeLabel = [];
        if (createdDateFrom && createdDateTo) {
            dateRangeLabel.push(`${createdDateFrom} to ${createdDateTo}`);
        } else if (createdDateFrom) {
            dateRangeLabel.push(`From ${createdDateFrom}`);
        } else if (createdDateTo) {
            dateRangeLabel.push(`To ${createdDateTo}`);
        }
        appliedFilters.push({
            key: 'createdDateRange',
            label: `Date Created: ${dateRangeLabel.join('')}`,
            onRemove: handleCreatedDateRangeRemove,
        });
    }
    if (updatedDateFrom || updatedDateTo) {
        const dateRangeLabel = [];
        if (updatedDateFrom && updatedDateTo) {
            dateRangeLabel.push(`${updatedDateFrom} to ${updatedDateTo}`);
        } else if (updatedDateFrom) {
            dateRangeLabel.push(`From ${updatedDateFrom}`);
        } else if (updatedDateTo) {
            dateRangeLabel.push(`To ${updatedDateTo}`);
        }
        appliedFilters.push({
            key: 'updatedDateRange',
            label: `Date Updated: ${dateRangeLabel.join('')}`,
            onRemove: handleUpdatedDateRangeRemove,
        });
    }

    // Format date helper
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    };

    // Get Type display from constants
    const getTypeDisplay = (type) => {
        return TYPE_DISPLAY_MAPPING[type?.toLowerCase()] || 'Unknown';
    };

    // Get Location name curerntUser.locations with given locationId
    const getLocationName = (locationId) => {
        const location = currentUser?.locations?.find((loc) => loc.locationId === locationId);
        return location ? location.name : 'N/A';
    };

    // Render the count buttons by status
    const renderActionButton = (count) => {
        if (count.status === 'IN_PROGRESS' || count.status == 'PENDING' || count.status === 'PREPARING') {
            return <Button icon={ViewIcon} variant="plain" onClick={() => navigate(`/cycle-counts/${count.id}/perform`)} accessibilityLabel={`View cycle count ${count.id}`} />;
        } else {
            return <Button icon={ViewIcon} variant="plain" onClick={() => navigate(`/cycle-counts/${count.id}`)} accessibilityLabel={`View cycle count ${count.id}`} />;
        }
    };

    // Table headings
    const headings = [
        { title: 'Cycle Count' },
        { title: 'Description' },
        { title: 'Assigned Store' },
        { title: 'Reason Code' },
        { title: 'Status' },
        { title: 'Type' },
        { title: 'Date Created' },
        { title: 'Date Updated' },
        { title: 'Actions' },
    ];

    // Row markup
    const rowMarkup = cycleCounts.map((count, index) => (
        <IndexTable.Row id={count.id?.toString()} key={count.id} position={index}>
            <IndexTable.Cell>
                <Text variant="bodyMd" fontWeight="semibold" as="span">
                    {count.name}
                </Text>
            </IndexTable.Cell>
            <IndexTable.Cell>{count.description || 'N/A'}</IndexTable.Cell>
            <IndexTable.Cell>{getLocationName(count.locationId)}</IndexTable.Cell>
            <IndexTable.Cell>{count.reason.reasonCode}</IndexTable.Cell>
            <IndexTable.Cell>
                <InlineStack gap="100">
                    <CustomBadge status={STATUS_DISPLAY_MAPPING[count.status]} />
                    {count.status === STATUS.IN_PROGRESS && count.rejectionReason && (
                        <Tooltip content={`Rejected: ${count.rejectionReason}`}>
                            <CustomBadge status={'Rejected'} />
                        </Tooltip>
                    )}
                </InlineStack>
            </IndexTable.Cell>
            <IndexTable.Cell>
                <CustomBadge status={getTypeDisplay(count.type)} />
            </IndexTable.Cell>
            <IndexTable.Cell>{formatDate(count.createdAt)}</IndexTable.Cell>
            <IndexTable.Cell>{formatDate(count.updatedAt)}</IndexTable.Cell>
            <IndexTable.Cell>{renderActionButton(count)}</IndexTable.Cell>
        </IndexTable.Row>
    ));

    // Empty state
    const emptyStateMarkup =
        isLoading && cycleCounts.length === 0 ? (
            <EmptySearchResult title="Loading cycle counts..." description="Please wait while we load your cycle counts." withIllustration />
        ) : cycleCounts.length === 0 ? (
            <EmptySearchResult title="No cycle counts found" description="There are no cycle counts to display. Try adjusting your filters or create a new cycle count." withIllustration />
        ) : null;

    let createNewCycleCountButton;
    if (canCreateCycleCount) {
        createNewCycleCountButton = (
            <Button variant="primary" onClick={() => navigate('/cycle-counts/create')}>
                Create New Count
            </Button>
        );
    }

    return (
        <Page fullWidth primaryAction={createNewCycleCountButton}>
            <Layout>
                <Layout.Section>
                    <Card>
                        <Box paddingBlock="400">
                            <Text variant="headingLg">Cycle Counts</Text>
                        </Box>
                        <IndexFilters
                            sortOptions={SORT_OPTIONS}
                            sortSelected={sortSelected}
                            queryValue={queryValue}
                            queryPlaceholder="Search Cycle Counts"
                            onQueryChange={handleQueryChange}
                            onQueryClear={handleQueryClear}
                            onSort={handleSortChange}
                            filters={filters}
                            appliedFilters={appliedFilters}
                            onClearAll={handleClearAll}
                            mode={mode}
                            setMode={setMode}
                            tabs={[]}
                            selected={0}
                            canCreateNewView={false}
                            cancelAction={{
                                onAction: handleClearAll,
                            }}
                            autoFocusSearchField={false}
                        />
                        <IndexTable condensed={breakpoints.smDown} resourceName={RESOURCE_NAME} itemCount={cycleCounts.length} headings={headings} selectable={false} emptyState={emptyStateMarkup}>
                            {rowMarkup}
                        </IndexTable>

                        <Box padding="400">
                            <InlineStack align="left" gap="200">
                                <Pagination
                                    hasPrevious={pagination.hasPrevious && !isLoading}
                                    hasNext={pagination.hasNext && !isLoading}
                                    onPrevious={() => handlePageChange(pagination.page - 1)}
                                    onNext={() => handlePageChange(pagination.page + 1)}
                                    label={`${(pagination.page - 1) * pagination.pageSize + 1}-${Math.min(pagination.page * pagination.pageSize, pagination.totalCount)} of ${pagination.totalCount}`}
                                />
                            </InlineStack>
                        </Box>
                    </Card>
                </Layout.Section>
            </Layout>
        </Page>
    );
};

export default CycleCountsDashboard;
